import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import Modal from '../../components/modal/Modal';
import { Col, convertToKRW, Row } from '../../lib/utils';
import { DemiLightText, MediumText, RegularText } from '../../lib/Text';
import { colors } from '../../lib/colors';
import SvgIcon from '../../components/SvgIcon';
import { RevenueAdjustment } from '../../api/salaryCalc';

type Props = {
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    /** 자동 집계 매출액 (totalSales + groupSales) */
    autoSales: number;
    /**
     * 백엔드가 산출한 자동 환불 합계. 차감 부호 포함(예: -200000). 환불이 없으면 0.
     * 지금은 쓰지 않는다 — 매출이 이미 순매출이라 새 환불 항목을 만들지 않고, 저장된 항목은 저장값 그대로 다시 보낸다.
     */
    refundAmount: number;
    /** 기존 저장된 조정 항목 (있으면 모달 열 때 채워짐) */
    initialAdjustments: RevenueAdjustment[];
    /** 저장 콜백 — 부모에서 state 업데이트 + 백엔드 저장 */
    onSave: (adjustments: RevenueAdjustment[]) => void;
};

type CustomDraft = {
    label: string;
    amountInput: string; // 음수 허용 (e.g. "-50000")
};

const parseAmount = (raw: string): number => {
    if (raw === '' || raw === '-' || raw === '+') return NaN;
    if (!/^-?\d+$/.test(raw)) return NaN;
    return parseInt(raw, 10);
};

const ModalRevenueAdjustment = ({
    setIsVisible,
    autoSales,
    initialAdjustments,
    onSave,
}: Props) => {
    /**
     * 「환불 금액 빼기」 선택지 — 서버 매출(totalSales)은 이미 환불을 뺀 순매출이라 이 항목이 있으면 환불이 두 번 빠진다.
     * - 저장된 조정에 환불 항목이 없으면 선택지 자체를 보이지 않는다(새로 넣지 못하게).
     * - 이미 있으면 체크된 채로 보이고 안내를 띄운다. 자동으로 풀지 않는다 — 원장이 풀고 저장해야 바로잡힌다.
     * 처음 열 때 값으로만 정한다(체크를 풀어도 선택지는 남아 있어 다시 체크할 수 있다).
     *
     * 체크된 채 저장하면 처음 열 때 저장돼 있던 환불 항목들을 금액·라벨·개수 그대로 다시 보낸다.
     * 지금의 refundAmount 로 새로 만들지 않는다 — 손대지 않고 저장했는데 급여가 바뀌면 안 된다.
     * 체크를 풀고 저장할 때만 그 항목들이 모두 빠진다.
     */
    const [initialRefundItems] = useState<RevenueAdjustment[]>(() =>
        initialAdjustments.filter((a) => a.reasonType === 'refund'),
    );
    const initialRefundChecked = initialRefundItems.length > 0;
    const [showRefundPreset] = useState<boolean>(initialRefundChecked);
    const savedRefundTotal = initialRefundItems.reduce((acc, cur) => acc + cur.amount, 0);
    const initialCustoms: CustomDraft[] = useMemo(
        () =>
            initialAdjustments
                .filter((a) => a.reasonType === 'custom')
                .map((a) => ({ label: a.label, amountInput: String(a.amount) })),
        [initialAdjustments],
    );

    const [isRefundChecked, setIsRefundChecked] = useState<boolean>(initialRefundChecked);
    const [customs, setCustoms] = useState<CustomDraft[]>(initialCustoms);

    const customAdjustments = useMemo<RevenueAdjustment[]>(() => {
        return customs
            .map((c) => ({
                reasonType: 'custom' as const,
                label: c.label.trim(),
                amount: parseAmount(c.amountInput),
            }))
            .filter((a) => a.label !== '' && !Number.isNaN(a.amount));
    }, [customs]);

    const previewAdjustments = useMemo<RevenueAdjustment[]>(() => {
        const list: RevenueAdjustment[] = [];
        if (isRefundChecked) {
            list.push(...initialRefundItems.map((item) => ({ ...item })));
        }
        list.push(...customAdjustments);
        return list;
    }, [isRefundChecked, initialRefundItems, customAdjustments]);

    const totalAdjustment = useMemo(
        () => previewAdjustments.reduce((acc, cur) => acc + cur.amount, 0),
        [previewAdjustments],
    );
    const effectiveSales = autoSales + totalAdjustment;

    const isDisabled = useMemo(() => {
        // 입력 중인 행이 있는데 라벨/금액 중 한쪽이 미완성이면 비활성화
        return customs.some((c) => {
            const labelOk = c.label.trim() !== '';
            const amountOk = !Number.isNaN(parseAmount(c.amountInput));
            return (labelOk && !amountOk) || (!labelOk && amountOk);
        });
    }, [customs]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && !isDisabled) {
                onSave(previewAdjustments);
                setIsVisible(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isDisabled, previewAdjustments, onSave, setIsVisible]);

    const addCustom = () => {
        setCustoms((prev) => [...prev, { label: '', amountInput: '' }]);
    };
    const removeCustom = (idx: number) => {
        setCustoms((prev) => prev.filter((_, i) => i !== idx));
    };
    const updateCustom = (idx: number, patch: Partial<CustomDraft>) => {
        setCustoms((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
    };

    return (
        <Modal setIsVisible={setIsVisible} whiteViewWidth={560} borderRadius={15}>
            <Col className="py-[36px] px-[33px]">
                <MediumText
                    fontSize={22}
                    lineHeight={38}
                    letterSpacing={-0.32}
                    color={colors.Text_1}
                    className="mb-[8px]"
                >
                    이번 달 매출액 조정
                </MediumText>
                <DemiLightText
                    fontSize={13}
                    lineHeight={19}
                    letterSpacing={-0.3}
                    color="#6B7280"
                    className="mb-[24px]"
                >
                    시스템이 자동으로 잡은 매출액(환불은 이미 빠져 있어요)에 누락된 매출을 더하거나 빼서 직접 맞춰주세요. 여기서 정한 금액으로 수업료 요율과 인센티브가 다시 계산됩니다.
                </DemiLightText>

                {/* 자동 집계 매출 */}
                <Row className="bg-[#F6F8FA] rounded-[10px] px-[18px] py-[14px] justify-between items-center mb-[20px]">
                    <RegularText fontSize={15} lineHeight={22} letterSpacing={-0.3} color="#5A6370">
                        자동으로 집계된 매출
                    </RegularText>
                    <Row className="items-end">
                        <RegularText fontSize={17} lineHeight={24} letterSpacing={-0.3} color={colors.Text_1} className="mr-[4px]">
                            {convertToKRW(autoSales)}
                        </RegularText>
                        <DemiLightText fontSize={14} lineHeight={20} letterSpacing={-0.3} color="#6B7280">
                            원
                        </DemiLightText>
                    </Row>
                </Row>

                {/* 변경 내역 */}
                <RegularText
                    fontSize={14}
                    lineHeight={20}
                    letterSpacing={-0.3}
                    color="#5A6370"
                    className="mb-[8px]"
                >
                    변경 내역
                </RegularText>
                {/* 프리셋: 환불 금액 — 저장된 환불 항목이 있을 때만(두 번 빠짐 정리용) */}
                {showRefundPreset && (
                <Col
                    className={`border border-solid rounded-[10px] px-[16px] py-[14px] mb-[10px] ${
                        isRefundChecked ? 'border-primary bg-[#F2F6FF]' : 'border-[#E6E8EB]'
                    }`}
                >
                    <Row className="justify-between items-center">
                        {/* 키보드로도 조작한다 — Space 로 체크/해제. Enter 는 창 전체 「저장」 단축키라 여기서 막는다(토글+저장이 겹치지 않게). */}
                        <button
                            type="button"
                            role="checkbox"
                            aria-checked={isRefundChecked}
                            className="flex flex-row items-center cursor-pointer bg-transparent border-0 p-0 m-0 text-left"
                            onClick={() => setIsRefundChecked((v) => !v)}
                            onKeyDown={(e) => {
                                // Enter 는 체크를 바꾸지 않는다(버튼 기본 클릭만 막음) — 창 전체의 Enter 저장은 그대로 동작해야 한다.
                                if (e.key === 'Enter') e.preventDefault();
                            }}
                        >
                            <Col
                                className={`w-[18px] h-[18px] rounded-[5px] mr-[10px] flex items-center justify-center ${
                                    isRefundChecked ? 'bg-primary' : 'border border-solid border-[#C9CDD2] bg-white'
                                }`}
                            >
                                {isRefundChecked && (
                                    <SvgIcon
                                        name="SvgCheck"
                                        size={10}
                                        fill={colors.trans}
                                        stroke="white"
                                        strokeWidth={2}
                                    />
                                )}
                            </Col>
                            <RegularText fontSize={15} lineHeight={22} letterSpacing={-0.3} color={colors.Text_1}>
                                환불 금액 빼기
                            </RegularText>
                            <div className="bg-[#FDECEA] rounded-full px-[8px] py-[2px] ml-[8px]">
                                <DemiLightText fontSize={13} lineHeight={18} color="#E57373">
                                    자동
                                </DemiLightText>
                            </div>
                        </button>
                        <Row className="items-end">
                            <RegularText
                                fontSize={16}
                                lineHeight={22}
                                letterSpacing={-0.3}
                                color={savedRefundTotal < 0 ? '#E57373' : colors.Text_1}
                                className="mr-[4px]"
                            >
                                {savedRefundTotal === 0 ? '0' : convertToKRW(savedRefundTotal)}
                            </RegularText>
                            <DemiLightText fontSize={13} lineHeight={19} color="#6B7280">
                                원
                            </DemiLightText>
                        </Row>
                    </Row>
                    {isRefundChecked && (
                    <DemiLightText
                        fontSize={13}
                        lineHeight={18}
                        letterSpacing={-0.3}
                        color="#6B7280"
                        className="mt-[6px] ml-[28px]"
                    >
                        저장된 환불 금액만큼 매출에서 빠져요
                    </DemiLightText>
                    )}
                    {isRefundChecked && (
                        <Row
                            className="items-start mt-[10px] px-[12px] py-[10px] bg-[#FFF8E1] rounded-[8px] gap-[8px]"
                            data-testid="refund-double-deduction-notice"
                        >
                            <SvgIcon name="SvgWarningInCircle" size={16} fill={'#F59E0B'} />
                            <RegularText fontSize={13} lineHeight={19} letterSpacing={-0.3} color="#92400E">
                                환불은 이미 매출에서 빠져 있어서 이 항목이 있으면 두 번 빠져요. 체크를 풀고 저장하면 바로잡혀요.
                            </RegularText>
                        </Row>
                    )}
                </Col>
                )}

                {/* 직접 입력 항목들 */}
                {customs.map((custom, idx) => {
                    const parsed = parseAmount(custom.amountInput);
                    const isNegative = !Number.isNaN(parsed) && parsed < 0;
                    return (
                        <Row
                            key={idx}
                            className="border border-solid border-[#E6E8EB] rounded-[10px] px-[14px] py-[10px] mb-[10px] items-center"
                            style={{ gap: 10 }}
                        >
                            <input
                                value={custom.label}
                                onChange={(e) => updateCustom(idx, { label: e.target.value })}
                                placeholder="조정 사유 (예: 외부 강의 매출)"
                                maxLength={20}
                                className="flex-1 font-noto-demilight text-[15px] leading-[22px] text-[#3D4244] bg-trans"
                            />
                            <Row className="items-center" style={{ gap: 4 }}>
                                <input
                                    value={custom.amountInput}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        if (raw === '' || raw === '-' || /^-?\d*$/.test(raw)) {
                                            updateCustom(idx, { amountInput: raw });
                                        }
                                    }}
                                    placeholder="금액 (음수 가능)"
                                    maxLength={12}
                                    className={`w-[150px] font-noto-regular text-[15px] leading-[22px] text-end bg-trans ${
                                        isNegative ? 'text-[#E57373]' : 'text-[#3D4244]'
                                    }`}
                                />
                                <DemiLightText fontSize={13} lineHeight={19} color="#6B7280">
                                    원
                                </DemiLightText>
                            </Row>
                            <Col
                                className="w-[24px] h-[24px] rounded-full bg-[#F2F4F6] flex items-center justify-center cursor-pointer"
                                onClick={() => removeCustom(idx)}
                            >
                                <SvgIcon name="SvgX" size={10} stroke="#71757C" strokeWidth={2} />
                            </Col>
                        </Row>
                    );
                })}

                {/* 직접 입력 추가 버튼 */}
                <Row
                    className="border border-dashed border-[#C9CDD2] rounded-[10px] px-[14px] py-[12px] items-center justify-center cursor-pointer mb-[24px]"
                    onClick={addCustom}
                >
                    <SvgIcon name="SvgPlus" size={12} fill="#71757C" className="mr-[6px]" />
                    <RegularText fontSize={14} lineHeight={20} letterSpacing={-0.3} color="#5A6370">
                        항목 직접 추가
                    </RegularText>
                </Row>

                {/* 조정 후 매출액 미리보기 */}
                <Col className="bg-[#F2F6FF] rounded-[12px] px-[18px] py-[16px] mb-[24px]">
                    <Row className="justify-between items-center mb-[6px]">
                        <DemiLightText fontSize={13} lineHeight={19} letterSpacing={-0.3} color="#5A6370">
                            변경 합계
                        </DemiLightText>
                        <DemiLightText
                            fontSize={14}
                            lineHeight={20}
                            letterSpacing={-0.3}
                            color={totalAdjustment < 0 ? '#E57373' : totalAdjustment > 0 ? '#12A25D' : colors.Text_1}
                        >
                            {totalAdjustment === 0
                                ? '0'
                                : `${totalAdjustment > 0 ? '+' : ''}${convertToKRW(totalAdjustment)}`}{' '}
                            원
                        </DemiLightText>
                    </Row>
                    <Row className="justify-between items-center">
                        <RegularText fontSize={15} lineHeight={22} letterSpacing={-0.3} color={colors.Text_1}>
                            최종 적용 매출액
                        </RegularText>
                        <Row className="items-end">
                            <MediumText
                                fontSize={20}
                                lineHeight={28}
                                letterSpacing={-0.3}
                                color={colors.primary}
                                className="mr-[4px]"
                            >
                                {convertToKRW(effectiveSales)}
                            </MediumText>
                            <DemiLightText fontSize={14} lineHeight={20} color="#6B7280">
                                원
                            </DemiLightText>
                        </Row>
                    </Row>
                </Col>

                <Row className="items-center self-end">
                    <Col
                        className="bg-GREY w-[110px] h-[41px] justify-center items-center rounded-[9px] mr-[9px] cursor-pointer"
                        onClick={() => setIsVisible(false)}
                    >
                        <RegularText fontSize={16} lineHeight={24} letterSpacing={-0.32} color={colors.Text_1}>
                            취소
                        </RegularText>
                    </Col>
                    <Col
                        className={`bg-primary w-[110px] h-[41px] justify-center items-center rounded-[9px] ${
                            isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                        }`}
                        onClick={() => {
                            if (isDisabled) return;
                            onSave(previewAdjustments);
                            setIsVisible(false);
                        }}
                    >
                        <RegularText fontSize={16} lineHeight={24} letterSpacing={-0.32} color={colors.white}>
                            저장
                        </RegularText>
                    </Col>
                </Row>
            </Col>
        </Modal>
    );
};

export default ModalRevenueAdjustment;
